import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Clock, BookOpen, Play, LogOut, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingApproval() {
    const { user, getAuthHeader, logout } = useAuth();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(false);
    const [orgName, setOrgName] = useState('');

    const checkStatus = useCallback(async () => {
        setChecking(true);
        try {
            const res = await axios.get(`${API}/auth/approval-status`, { headers: getAuthHeader() });
            if (res.data.approval_status === 'approved') {
                toast.success('Your access has been approved!');
                // Force a full reload to refresh user context
                window.location.href = '/dashboard';
                return;
            }
            if (res.data.org_name) setOrgName(res.data.org_name);
        } catch {
            // Ignore errors
        } finally {
            setChecking(false);
        }
    }, [getAuthHeader]);

    // Auto-poll every 30 seconds
    useEffect(() => {
        checkStatus();
        const interval = setInterval(checkStatus, 30000);
        return () => clearInterval(interval);
    }, [checkStatus]);

    // Redirect if user is already approved
    useEffect(() => {
        if (user && user.approval_status !== 'pending') {
            navigate('/dashboard', { replace: true });
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-lg">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8">
                    {/* Status Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                            <Clock className="w-8 h-8 text-amber-600" />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold text-stone-900 text-center mb-3" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Access Pending Approval
                    </h2>

                    <p className="text-sm text-stone-500 text-center mb-2 leading-relaxed">
                        Your organization{orgName ? ` (${orgName})` : ''} admin has been notified to approve your access.
                        You'll receive an email once approved.
                    </p>

                    <p className="text-xs text-stone-400 text-center mb-8">
                        We check automatically every 30 seconds.
                    </p>

                    {/* Check Status Button */}
                    <button
                        onClick={checkStatus}
                        disabled={checking}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 transition-colors disabled:opacity-50 mb-6"
                    >
                        <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                        {checking ? 'Checking...' : 'Check Status'}
                    </button>

                    {/* Divider */}
                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-stone-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-white px-3 text-stone-400">while you wait</span>
                        </div>
                    </div>

                    {/* Video Placeholder */}
                    <div className="mb-4 rounded-xl bg-stone-100 border border-stone-200 overflow-hidden">
                        <div className="aspect-video flex flex-col items-center justify-center gap-3">
                            <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                                <Play className="w-6 h-6 text-stone-400 ml-0.5" />
                            </div>
                            <p className="text-sm text-stone-400 font-medium">Product tour coming soon</p>
                        </div>
                    </div>

                    {/* Help Docs Link */}
                    <Link
                        to="/help"
                        className="w-full flex items-center justify-center gap-2 py-2.5 border border-stone-200 text-stone-700 font-medium rounded-lg hover:bg-stone-50 transition-colors"
                    >
                        <BookOpen className="w-4 h-4" />
                        Learn about NurtureCRM
                    </Link>
                </div>

                {/* Sign Out */}
                <div className="text-center mt-6">
                    <button
                        onClick={() => { logout(); navigate('/login'); }}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-600 transition-colors"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}
